import {
  resolvePublicMediaContract,
  resolvePublicMediaCover,
  resolvePublicMediaItems,
  resolvePublicMediaPrimary,
} from "./publicMediaContract";

export const PUBLIC_GALLERY_CONTRACT_VERSION = "public-gallery-v1";
export const PUBLIC_GALLERY_SOURCE = "image-asset-binding";

const resolveInventoryMediaContract = (entity, inventoryMediaEnabled) => {
  const contract = inventoryMediaEnabled ? entity?.inventoryMedia : null;
  if (!Array.isArray(contract?.items) || !contract.items.length) return null;
  return contract;
};

const resolveExperienceMediaContract = (entity, experienceMediaEnabled) => {
  const contract = experienceMediaEnabled ? entity?.experienceMedia : null;
  if (!Array.isArray(contract?.items) || !contract.items.length) return null;
  return contract;
};

const isCanonicalSource = (value) =>
  value?.sourceOfTruth === PUBLIC_GALLERY_SOURCE && value?.legacyFallback === false;

export const resolvePublicGalleryContract = (entity) => {
  const contract = entity?.publicGallery;
  if (
    contract?.contractVersion === PUBLIC_GALLERY_CONTRACT_VERSION &&
    isCanonicalSource(contract) &&
    Array.isArray(contract.items)
  ) {
    return contract;
  }
  return null;
};

// `null` means the response predates the public-gallery contract. An empty
// array is authoritative and must not fall through to a legacy media field.
export const resolvePublicGalleryItems = (
  entity,
  { inventoryMediaEnabled = false, experienceMediaEnabled = false } = {},
) => {
  const publicMediaItems = resolvePublicMediaItems(entity);
  if (publicMediaItems) return publicMediaItems;

  const experienceContract = resolveExperienceMediaContract(entity, experienceMediaEnabled);
  if (experienceContract) return experienceContract.items;
  const inventoryContract = resolveInventoryMediaContract(entity, inventoryMediaEnabled);
  if (inventoryContract) return inventoryContract.items;

  const contract = resolvePublicGalleryContract(entity);
  if (contract) return contract.items;

  const transitional = entity?.canonicalImageDelivery;
  if (isCanonicalSource(transitional) && Array.isArray(transitional.gallery)) {
    return transitional.gallery;
  }
  return null;
};

// The primary image is a separate entity-preset binding. Gallery items use the
// gallery-media preset and cannot satisfy card/hero surface slots.
export const resolvePublicPrimaryImage = (
  entity,
  { inventoryMediaEnabled = false, experienceMediaEnabled = false } = {},
) => {
  const publicMedia = resolvePublicMediaContract(entity);
  if (publicMedia) return resolvePublicMediaPrimary(entity);

  const experienceContract = resolveExperienceMediaContract(entity, experienceMediaEnabled);
  if (experienceContract) {
    return experienceContract.featured?.main || experienceContract.items[0] || null;
  }
  const inventoryContract = resolveInventoryMediaContract(entity, inventoryMediaEnabled);
  if (inventoryContract) {
    return inventoryContract.featured?.main || inventoryContract.items[0] || null;
  }

  const contract = resolvePublicGalleryContract(entity);
  if (contract) return contract.primary || null;

  const transitional = entity?.canonicalImageDelivery;
  if (isCanonicalSource(transitional)) {
    return transitional.primary || null;
  }
  return null;
};

// Card and listing surfaces use the first published gallery item as their
// cover. The separate primary binding remains reserved for detail heroes.
export const resolvePublicCoverImage = (
  entity,
  { inventoryMediaEnabled = false, experienceMediaEnabled = false } = {},
) => {
  const publicMedia = resolvePublicMediaContract(entity);
  if (publicMedia) return resolvePublicMediaCover(entity);

  const contract = resolvePublicGalleryContract(entity);
  if (contract?.cover) return contract.cover;

  const transitional = entity?.canonicalImageDelivery;
  if (isCanonicalSource(transitional) && transitional.gallery?.[0]) {
    return transitional.gallery[0];
  }

  const experienceContract = resolveExperienceMediaContract(entity, experienceMediaEnabled);
  if (experienceContract) {
    return experienceContract.featured?.main || experienceContract.items[0] || null;
  }
  const inventoryContract = resolveInventoryMediaContract(entity, inventoryMediaEnabled);
  if (inventoryContract) {
    return inventoryContract.featured?.main || inventoryContract.items[0] || null;
  }

  if (contract) return contract.cover || contract.items[0] || contract.primary || null;

  if (isCanonicalSource(transitional)) {
    return transitional.gallery?.[0] || transitional.primary || null;
  }
  return null;
};
